import type { CaptureSettings } from '../types';

/**
 * Apply image transformations (rotation, perspective, tilt, yaw) to a canvas
 */
export function applyImageTransformations(
  canvas: HTMLCanvasElement,
  settings: CaptureSettings
): HTMLCanvasElement {
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  // If no transformations needed, return original
  if (
    settings.rotation === 0 &&
    settings.perspectiveX === 0 &&
    settings.perspectiveY === 0 &&
    settings.tilt === 0 &&
    settings.yaw === 0 &&
    settings.zoom === 0
  ) {
    return canvas;
  }

  // Create a new canvas for the transformed image
  const outputCanvas = document.createElement('canvas');
  const outputCtx = outputCanvas.getContext('2d');
  if (!outputCtx) return canvas;

  // Calculate zoom scale (0-100 maps to 1.0-2.0)
  const zoomScale = settings.zoom > 0 ? 1 + (settings.zoom / 100) : 1;
  
  // Calculate required canvas size to accommodate all transformations
  const angleRad = Math.abs(settings.rotation) * Math.PI / 180;
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);
  
  // Base size for rotation
  let newWidth = Math.abs(canvas.width * cos) + Math.abs(canvas.height * sin);
  let newHeight = Math.abs(canvas.width * sin) + Math.abs(canvas.height * cos);
  
  // Add padding for perspective and 3D transformations
  const maxPerspective = Math.max(
    Math.abs(settings.perspectiveX),
    Math.abs(settings.perspectiveY)
  );
  const max3D = Math.max(Math.abs(settings.tilt), Math.abs(settings.yaw));
  const padding = Math.max(maxPerspective * 2, max3D * 5);
  
  // Output canvas size (same regardless of zoom - zoom crops the source)
  outputCanvas.width = Math.ceil(newWidth + padding);
  outputCanvas.height = Math.ceil(newHeight + padding);

  // Center the transformation
  const centerX = outputCanvas.width / 2;
  const centerY = outputCanvas.height / 2;
  const imgCenterX = canvas.width / 2;
  const imgCenterY = canvas.height / 2;

  outputCtx.save();
  outputCtx.translate(centerX, centerY);

  // Apply rotation first
  if (settings.rotation !== 0) {
    outputCtx.rotate(settings.rotation * Math.PI / 180);
  }

  // Apply perspective transformations using transform matrix
  // Perspective creates a "keystone" effect
  if (settings.perspectiveX !== 0 || settings.perspectiveY !== 0) {
    // Use transform to create perspective effect
    // Perspective X: horizontal keystone (left/right edges converge)
    // Perspective Y: vertical keystone (top/bottom edges converge)
    
    const px = settings.perspectiveX * 0.001; // Scale factor
    const py = settings.perspectiveY * 0.001;
    
    // Create perspective transform matrix
    // For horizontal perspective: skew based on Y position
    // For vertical perspective: skew based on X position
    const matrix = new DOMMatrix();
    matrix.a = 1 + py * imgCenterY; // Horizontal scale based on vertical position
    matrix.b = px * imgCenterY; // Horizontal skew
    matrix.c = py * imgCenterX; // Vertical skew
    matrix.d = 1 + px * imgCenterX; // Vertical scale based on horizontal position
    matrix.e = 0;
    matrix.f = 0;
    
    outputCtx.setTransform(matrix);
  }

  // Apply tilt (rotation around X-axis) - creates vertical perspective
  if (settings.tilt !== 0) {
    const tiltRad = settings.tilt * Math.PI / 180;
    const currentTransform = outputCtx.getTransform();
    
    // Tilt creates a perspective where top/bottom edges converge
    // This is achieved by scaling Y based on distance from center
    const tiltFactor = Math.tan(tiltRad) * 0.01; // Scale for reasonable effect
    
    const matrix = new DOMMatrix();
    matrix.a = currentTransform.a;
    matrix.b = currentTransform.b + tiltFactor;
    matrix.c = currentTransform.c;
    matrix.d = currentTransform.d;
    matrix.e = currentTransform.e;
    matrix.f = currentTransform.f;
    
    outputCtx.setTransform(matrix);
  }

  // Apply yaw (rotation around Y-axis) - creates horizontal perspective
  if (settings.yaw !== 0) {
    const yawRad = settings.yaw * Math.PI / 180;
    const currentTransform = outputCtx.getTransform();
    
    // Yaw creates a perspective where left/right edges converge
    const yawFactor = Math.tan(yawRad) * 0.01;
    
    const matrix = new DOMMatrix();
    matrix.a = currentTransform.a;
    matrix.b = currentTransform.b;
    matrix.c = currentTransform.c + yawFactor;
    matrix.d = currentTransform.d;
    matrix.e = currentTransform.e;
    matrix.f = currentTransform.f;
    
    outputCtx.setTransform(matrix);
  }

  // Apply zoom (scale factor based on 0-100 range)
  // 0 = no zoom (1x), 100 = 2x zoom
  // Zoom scales the image up, showing a cropped center portion
  if (settings.zoom > 0) {
    // Scale the context to zoom in - this will make everything drawn appear larger
    outputCtx.scale(zoomScale, zoomScale);
    
    // Draw the image at its original size
    // Because the context is scaled, it will appear zoomScale times larger
    // and be cropped by the canvas bounds, showing the center portion
    outputCtx.drawImage(
      canvas,
      -imgCenterX,
      -imgCenterY,
      canvas.width,
      canvas.height
    );
  } else {
    // No zoom - draw the full image
    outputCtx.drawImage(
      canvas,
      -imgCenterX,
      -imgCenterY,
      canvas.width,
      canvas.height
    );
  }

  outputCtx.restore();

  return outputCanvas;
}

/**
 * Get CSS transform string for real-time preview
 */
export function getTransformCSS(settings: CaptureSettings): string {
  const transforms: string[] = [];

  if (settings.rotation !== 0) {
    transforms.push(`rotate(${settings.rotation}deg)`);
  }

  if (settings.perspectiveX !== 0 || settings.perspectiveY !== 0) {
    // Use skew for perspective effect
    if (settings.perspectiveX !== 0) {
      transforms.push(`skewY(${settings.perspectiveX * 0.1}deg)`);
    }
    if (settings.perspectiveY !== 0) {
      transforms.push(`skewX(${settings.perspectiveY * 0.1}deg)`);
    }
  }

  if (settings.tilt !== 0) {
    // Tilt as perspective transform
    transforms.push(`perspective(1000px) rotateX(${settings.tilt}deg)`);
  }

  if (settings.yaw !== 0) {
    // Yaw as perspective transform
    transforms.push(`perspective(1000px) rotateY(${settings.yaw}deg)`);
  }

  if (settings.zoom > 0) {
    // Zoom: 0-100 maps to scale 1.0-2.0
    const zoomScale = 1 + (settings.zoom / 100);
    transforms.push(`scale(${zoomScale})`);
  }

  return transforms.length > 0 ? transforms.join(' ') : 'none';
}

/**
 * Get CSS filter string for real-time brightness/contrast preview
 */
export function getEnhancementCSS(settings: CaptureSettings): string {
  const filters: string[] = [];

  if (settings.autoBrightness) {
    // Auto brightness is calculated per-image, so we use a moderate adjustment
    // The actual value will be calculated when capturing
    filters.push('brightness(1.1)');
  }

  if (settings.contrastBoost !== 0) {
    // Convert -1 to 1 range to CSS contrast (0.5 to 1.5)
    const contrast = 1 + settings.contrastBoost * 0.5;
    filters.push(`contrast(${contrast})`);
  }

  if (settings.sharpen) {
    // CSS doesn't have native sharpen, but we can use a subtle contrast boost
    filters.push('contrast(1.1) saturate(1.1)');
  }

  return filters.length > 0 ? filters.join(' ') : 'none';
}

